// CRITICAL: Sentry must be imported FIRST before any other code
import { BRAND_SITE_HOST, BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import "./sentry.client.config";

import posthog from "posthog-js";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import "./i18n/config";
import { installSiteDebugGlobals } from "./lib/siteDebug";
import { initAhrefsAnalytics } from "./lib/ahrefsAnalytics";
import { initDatadogRum } from "./lib/datadogRum";
import { initAnalyticsErrorHandler } from "./lib/analyticsErrorHandler";
import { initAnalyticsLoader } from "./lib/loadAnalytics";
import { isAdminShellPath } from "./lib/geoConsentRoutes";
import { preloadClerkForAdmin } from "./lib/preloadClerkForAdmin";
import {
  captureEntryPageUrlPostHog,
  initPersonPropertiesPostHog,
  registerSuperPropertiesPostHog,
  startPostHogGeoRefresh,
  syncPostHogConsentFromStorage,
  trackErrorPostHog,
  trackWebVitalsPostHog,
} from "./lib/posthogAnalytics";
import { edgeRuntimeString } from "./lib/edgeRuntimeConfig";
import { prefetchBrandRuntimeConfig } from "./lib/brandRuntimeConfig";
import {
  registerServiceWorker,
  unregisterServiceWorker,
} from "./lib/registerServiceWorker";
import { enrichPostHogEventBeforeSend } from "./lib/posthogEngagementContext";
import { applyPostHogSessionPolicy, isPostHogDisabledPath } from "./lib/posthogPathGuard";
import { setupCspViolationReporting } from "./lib/cspReporting";
import { CSP_VERSION } from "./config/csp";

// Site debug globals only needed for public-site console diagnostics.
// On admin shell: skip (less work + no CTA/i18n visitor noise in DevTools).
if (typeof window === "undefined" || !isAdminShellPath(window.location.pathname)) {
  installSiteDebugGlobals();
}

// URL de entrada de la sesión — el checkout la manda como metadata `entry_page`
// para que el Slack de venta muestre por dónde entró el comprador. Debe correr
// ANTES de cualquier navegación SPA (react-router pisa location).
if (typeof window === "undefined" || !isAdminShellPath(window.location.pathname)) {
  captureEntryPageUrlPostHog();
}

void prefetchBrandRuntimeConfig();

// PWA: SW mínimo (instalabilidad + offline). No cachea JS/CSS — ver public/sw.js.
registerServiceWorker();
if (typeof window !== "undefined") {
  window.__unregisterSW = unregisterServiceWorker;
}

if (typeof window !== "undefined" && isAdminShellPath(window.location.pathname)) {
  preloadClerkForAdmin();
}

// Silence blocked analytics console noise — only on public site (admin doesn't load those tags).
if (typeof window === "undefined" || !isAdminShellPath(window.location.pathname)) {
  initAnalyticsErrorHandler();
}

// Datadog RUM (us5) — no-op sin VITE_DD_RUM_*; carga en idle (dynamic import),
// replay apagado (PostHog es el owner). Corre en público Y admin: el bridge
// window.DD_RUM alimenta adminObservability (admin_api_request/addError).
initDatadogRum();

// Ahrefs Web Analytics: early ensure (primary tag is gated in index.html for public paths).
// Not gated on cookie consent (Ahrefs is cookieless / no PII by default).
if (typeof window !== "undefined" && !isAdminShellPath(window.location.pathname)) {
  initAhrefsAnalytics();
}

// Initialize lazy loading of third-party analytics (GA, Meta Pixel, Metricool, Tally)
// These load after user interaction or 4s idle for better FCP/LCP — skip on admin shell.
const isDualCtaAdmin = typeof window !== "undefined" && (
  window.location.pathname.startsWith("/admin/dual-cta-home") ||
  window.location.pathname.startsWith("/admin/dual-cta-templates") ||
  window.location.pathname.startsWith("/admin/video-ab-slots")
);
if (typeof window === "undefined" || (!isAdminShellPath(window.location.pathname) || isDualCtaAdmin)) {
  initAnalyticsLoader();
}

// PostHog + Session Replay: public site only — never on paths containing /admin.

// Import debug utilities in development
if (import.meta.env.DEV) {
  try {
    import("./utils/debugCountryTemplates");
  } catch (error) {
  }
}

const posthogKey =
  edgeRuntimeString("posthogKey") ||
  (import.meta.env.VITE_POSTHOG_KEY as string) ||
  "";
const posthogHost =
  edgeRuntimeString("posthogHost") ||
  (import.meta.env.VITE_POSTHOG_HOST as string) ||
  (import.meta.env.DEV ? "/ingest" : "/ingest");

const skipPostHogInit =
  typeof window !== "undefined" && isPostHogDisabledPath(window.location.pathname);

// Gate CSP violation reporting (with session/distinct linking) the same way as
// the rest of PostHog features: only on public site paths. This keeps the
// "CSP observability" feature aligned with session recording + analytics.
if (!skipPostHogInit) {
  setupCspViolationReporting();
}

if (typeof posthogKey === "string" && posthogKey.length > 0 && !skipPostHogInit) {
  try {
    // Ensure PostHog is available on window immediately
    if (typeof window !== "undefined") {
      (window as any).posthog = posthog;
    }
    
    posthog.init(posthogKey, {
      // Reverse proxy for capture; ui_host must be the PostHog app for replay/toolbar links.
      // Without ui_host, get_session_replay_url() returns /ingest/project/phc_…/replay/… (broken in Slack).
      api_host: posthogHost,
      ui_host: "https://us.posthog.com",
      before_send: enrichPostHogEventBeforeSend,
      // Session Replay: explícito; el dashboard del proyecto también debe tener «Record user sessions» activo.
      disable_session_recording: false,
      // Person profiles: 'always' creates profiles for all visitors (including anonymous)
      // This is required to see session replays and user properties in PostHog for any user
      person_profiles: 'always',
      // Alineado con la plantilla de instalación PostHog (project defaults)
      defaults: "2026-05-30",
      // SPA: we capture pageviews on route changes via <PostHogPageview />
      capture_pageview: false,
      capture_pageleave: true,
      // Session recording - enabled for ALL templates (BaseTemplate, BioLinkTemplate, ArgentinaBioTemplate)
      session_recording: {
        recordCrossOriginIframes: false,
        recordHeaders: false,
        recordBody: false,
        maskCapturedNetworkRequestFn: (request: any) => {
          if (!request) return null;

          // Skip analytics/telemetry endpoints
          const url = request.url || request.name || "";
          const skipUrls = [
            "axiom.co",
            "inferred.litix.io",
            "posthog.com",
            "us.i.posthog.com",
            "eu.i.posthog.com",
          ];
          if (skipUrls.some((s) => url.includes(s))) return null;

          const sensitiveHeaders = [
            'authorization', 'x-api-key', 'api-key', 'x-auth-token',
            'cookie', 'set-cookie', 'x-csrf-token', 'csrf-token',
            'x-session-id', 'session-id', 'x-access-token', 'access-token',
            'x-refresh-token', 'refresh-token', 'stripe-signature', 'x-stripe-signature',
          ];

          if (request.requestHeaders) {
            sensitiveHeaders.forEach(header => {
              const lowerHeader = header.toLowerCase();
              if (request.requestHeaders.has?.(lowerHeader)) {
                request.requestHeaders.set(lowerHeader, '[REDACTED]');
              }
            });
          }
          if (request.responseHeaders) {
            sensitiveHeaders.forEach(header => {
              const lowerHeader = header.toLowerCase();
              if (request.responseHeaders.has?.(lowerHeader)) {
                request.responseHeaders.set(lowerHeader, '[REDACTED]');
              }
            });
          }

          return request;
        },
        // Recording strategy (2026-06-06): record 100% of sessions. Earlier
        // 30% sample rate plus project-level 5s minimum duration was masking
        // ~50% of sessions including bouncers that reveal UX friction. With
        // ~8 sessions/24h of organic traffic, storage cost is negligible and
        // full coverage is more valuable. Project-level url_trigger_config
        // was also cleared (it required exact "/" URL and silently disabled
        // any visitor arriving with ?utm_source=...). The url_blocklist for
        // /admin is preserved.
        maskAllInputs: true,
        maskTextSelector: '[data-ph-mask]',
      },
      // Autocapture - enabled to track all events on all templates
      autocapture: {
        css_selector_allowlist: ["[data-attr]", "button", "a", "[data-ph-capture]", "[data-ph-section]"],
        url_allowlist: [], // Capture on all URLs (empty = all URLs)
        dom_event_allowlist: ["click", "change", "submit"], // Capture clicks, form changes, and submits
        capture_copied_text: true, // Capture copied text
      },
      rageclick: true,
      capture_dead_clicks: true,
      // Enable feature flags
      bootstrap: {},
      loaded: (posthogInstance) => {
        // Ensure PostHog is available globally
        if (typeof window !== "undefined") {
          (window as any).posthog = posthogInstance;
        }

        // Notify CSP reporter (and others) that PostHog is ready so live session/distinct ids can be attached.
        try {
          window.dispatchEvent(new CustomEvent("ph:posthog_ready"));
        } catch {
          /* noop */
        }
        
        // Debug mode in development
        if (import.meta.env.DEV) {
          posthogInstance.debug(true);
        }

        // Register super properties — auto-attached to every future event
        registerSuperPropertiesPostHog();

        // Async IPLocate privacy enrichment (Worker inject may be cf_only on cache miss)
        startPostHogGeoRefresh();

        // Enrich person profile with custom properties + session count
        initPersonPropertiesPostHog();

        // Session Replay: anónimo en sitio público; Clerk admin se excluye en PostHogPageview.
        // /decide llega async — re-assert start at 0ms / 1.5s / 4s so recorder is not stuck off.
        try {
          syncPostHogConsentFromStorage();
          applyPostHogSessionPolicy(window.location.pathname, null);
          const reassertRecording = () => {
            try {
              if (isPostHogDisabledPath(window.location.pathname)) return;
              applyPostHogSessionPolicy(window.location.pathname, null);
            } catch {
              /* ignore */
            }
          };
          window.setTimeout(reassertRecording, 1500);
          window.setTimeout(reassertRecording, 4000);
        } catch {
          /* ignore */
        }

        // Observe Core Web Vitals (LCP, FCP, CLS, TTFB, INP)
        trackWebVitalsPostHog();

        // Global error tracking — wire up unhandled JS errors and promise rejections
        window.addEventListener("error", (event) => {
          const error = event.error instanceof Error
            ? event.error
            : new Error(event.message || "Unknown error");
          trackErrorPostHog(error, {
            error_type: "uncaught_exception",
            source: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          });
        });

        window.addEventListener("unhandledrejection", (event) => {
          const error = event.reason instanceof Error
            ? event.reason
            : new Error(String(event.reason ?? "Unhandled rejection"));
          trackErrorPostHog(error, { error_type: "unhandled_rejection" });
        });
        
        // Send initialization event
        try {
          const recordingEnabled =
            posthogInstance.sessionRecordingStarted?.() ||
            !posthog.config?.disable_session_recording;
          posthogInstance.capture('posthog_initialized', {
            timestamp: new Date().toISOString(),
            autocapture_enabled: Boolean(posthog.config?.autocapture),
            session_recording_enabled: Boolean(recordingEnabled),
            environment: import.meta.env.MODE || 'production',
            csp_version: CSP_VERSION,
          });
        } catch (error) {
          console.error(
            "[PostHog] posthog_initialized capture failed:",
            error instanceof Error ? error.message : String(error),
          );
        }

        // Record the active CSP version for this session (helps filter by policy revision).
        try {
          posthogInstance.capture("csp_config", { csp_version: CSP_VERSION });
        } catch {
          /* noop */
        }
      },
      // Privacy settings
      // DNT: keep true for privacy tools, but DualCTA forces opt_in + flush on
      // video_viewed (see trackDualCta) so short YouTube/desktop bounces still
      // leave a session trail when the user has not declined cookies.
      respect_dnt: true,
      // Opt-in: no capture until cookie banner Accept (syncPostHogConsentFromStorage).
      opt_out_capturing_by_default: true,
      opt_out_persistence_by_default: true,
      // Performance settings
      request_batching: true,
      // Cross-domain tracking
      cross_subdomain_cookie: false,
      secure_cookie: true,
      // Persistence
      persistence: "localStorage+cookie",
      persistence_name: `ph_${BRAND_SOURCE_KEY}`,
      // Advanced settings
      property_blacklist: [], // Properties to exclude
      sanitize_properties: null, // Function to sanitize properties
      xhr_headers: {}, // Custom headers for requests
    });
  } catch (error) {
    // Continue without PostHog if it fails (e.g. blocked by adblocker). NO silenciar
    // en prod: un fallo de init acá deja al sitio sin analytics y nos costó días
    // diagnosticarlo. Log siempre para que sea visible en la consola del navegador.
    console.error(
      "[PostHog] init failed — analytics disabled:",
      error instanceof Error ? error.message : String(error),
    );
  }
} else if (skipPostHogInit) {
  if (typeof window !== "undefined") {
    (window as any).posthog = undefined;
  }
} else {
  // Log when PostHog is not configured
  if (import.meta.env.DEV) {
    console.warn("PostHog not configured - VITE_POSTHOG_KEY not found, continuing without analytics");
  }

  // Ensure window.posthog is undefined when not configured
  if (typeof window !== "undefined") {
    (window as any).posthog = undefined;
  }
}

// Always make safe capture function available, even if PostHog is not configured
if (typeof window !== "undefined") {
  // Safe PostHog capture helper - checks if PostHog is ready before capturing
  (window as any).posthogSafeCapture = (eventName: string, properties?: Record<string, unknown>) => {
    if (typeof window === 'undefined') {
      return false;
    }
    if (isPostHogDisabledPath(window.location.pathname)) {
      return false;
    }

    const posthog = (window as any).posthog;
    if (!posthog) {
      return false;
    }
    
    if (typeof posthog.capture !== 'function') {
      return false;
    }
    
    try {
      posthog.capture(eventName, properties);
      return true;
    } catch (error) {
      return false;
    }
  };

  // Helper to check if PostHog is ready
  (window as any).isPostHogReady = () => {
    if (typeof window === 'undefined') return false;
    if (isPostHogDisabledPath(window.location.pathname)) return false;
    const posthog = (window as any).posthog;
    return !!(posthog && typeof posthog.capture === 'function' && posthog.__loaded);
  };
}

// Helper functions for region and country testing (no copy/paste needed!)
if (typeof window !== "undefined") {
  (window as any).setRegion = (region: "US" | "SPAIN" | "LATAM" | "OTHER") => {
    localStorage.setItem("user_region", region);
    localStorage.removeItem("test_country"); // Clear country test
    window.location.reload();
  };

  (window as any).testCountry = (countryCode: string) => {
    localStorage.setItem("user_region", "LATAM");
    localStorage.setItem("test_country", countryCode);
    // Simulate browser language
    Object.defineProperty(navigator, "language", {
      writable: true,
      value: `es-${countryCode}`,
    });
    window.location.reload();
  };

  // Test Cloudflare Enterprise geolocation (PRIMARY METHOD)
  (window as any).testCloudflareGeo = async () => {
    if (import.meta.env.DEV) {
      console.log(`🚀 Testing Cloudflare Enterprise geolocation (PRIMARY for ${BRAND_SITE_HOST})...`);
      console.log("   Cloudflare marked as unavailable:", !!(window as any).cloudflareUnavailable);
    }

    console.log("📍 Template configuration is injected by Cloudflare Worker");
    console.log("🔧 Check window.__TEMPLATE_CONFIG__ for current configuration");
    if ((window as any).__TEMPLATE_CONFIG__) {
      console.log("Current config:", (window as any).__TEMPLATE_CONFIG__);
    }
  };

  // Check Cloudflare status
  (window as any).checkCloudflareStatus = async () => {
    try {
      const response = await fetch("/api/cloudflare-status");
      const status = await response.json();
      if (import.meta.env.DEV) {
        console.log("   - Behind Cloudflare:", status.cloudflare.present ? "✅ YES" : "❌ NO");
        console.log("   - Geolocation Headers:", status.geolocation.hasCountry ? "✅ YES" : "❌ NO");
        console.log("   - True-Client-IP:", status.cloudflare.hasTrueClientIp ? "✅ YES" : "❌ NO");
      }
      if (!status.cloudflare.present && import.meta.env.DEV) {
        console.log("   Solution: Configure domain in Cloudflare and update DNS");
      } else if (!status.geolocation.hasCountry && import.meta.env.DEV) {
        console.log("   Solution: Enable 'Add visitor location headers' in Cloudflare Managed Transforms");
      } else if (status.cloudflare.present && status.geolocation.hasCountry && import.meta.env.DEV) {
        console.log("✅ SUCCESS: Cloudflare and geolocation are properly configured!");
      }
    } catch (error) {
    }
  };

  // Reset Cloudflare availability status
  (window as any).resetCloudflareStatus = () => {
    (window as any).cloudflareUnavailable = false;
    if (import.meta.env.DEV) {
      console.log("🔄 Cloudflare availability status reset - will try again on next detection");
    }
  };

  // Force Cloudflare test (ignore unavailable flag)
  (window as any).forceCloudflareTest = async () => {
    const wasUnavailable = (window as any).cloudflareUnavailable;
    (window as any).cloudflareUnavailable = false;

    await (window as any).testCloudflareGeo();

    // Restore the flag
    (window as any).cloudflareUnavailable = wasUnavailable;
  };

  // Test external geolocation API (EMERGENCY BACKUP ONLY)
  (window as any).testExternalGeo = async () => {
    if (import.meta.env.DEV) {
      console.log(`🚨 Testing EXTERNAL geolocation API (should NOT be primary for ${BRAND_SITE_HOST})...`);
      console.log("   ⚠️  This uses paid/limited APIs - Cloudflare Enterprise should handle this");
    }
    try {
      const response = await fetch("/api/geolocate");
      const geoData = await response.json();
      if (geoData.country) {
        if (import.meta.env.DEV) console.log("   ⚠️  WARNING: Using external API instead of Cloudflare Enterprise");
        const { getTemplateForCountry } = await import("@/config/countryTemplates");
        getTemplateForCountry(geoData.country);
      } else if (import.meta.env.DEV) {
        console.log("   💡 This means NO geolocation is working - check all setups");
      }
    } catch (error) {
    }
  };

  // Quick Cloudflare diagnosis
  (window as any).diagnoseCloudflare = async () => {
    try {
      const statusResponse = await fetch("/api/cloudflare-status");
      const status = await statusResponse.json();
      if (import.meta.env.DEV) {
        console.log("   Result:", status.cloudflare.present ? "✅ CLOUDFLARE ACTIVE" : "❌ NO CLOUDFLARE");
        console.log("   CF-RAY present:", status.cloudflare.hasCfRay ? "✅ YES" : "❌ NO");
        console.log("   True-Client-IP:", status.cloudflare.hasTrueClientIp ? "✅ YES" : "❌ NO");
      }
      if (!status.cloudflare.present && import.meta.env.DEV) {
        console.log("   💡 SOLUTION: Check DNS nameservers point to Cloudflare");
        console.log("   🔗 HOW TO: CLOUDFLARE_DIAGNOSIS.md section 'Agregar Dominio a Cloudflare'");
      } else if (status.cloudflare.present && !status.geolocation.hasCountry && import.meta.env.DEV) {
        console.log("   ⚠️  WARNING: Cloudflare active but no geolocation headers!");
        console.log("   💡 SOLUTION: Enable 'Add visitor location headers' in Managed Transforms");
        console.log("   🔗 HOW TO: CLOUDFLARE_DIAGNOSIS.md section 'Configuración de Zone Settings'");
      }
    } catch (error) {
    }
    try {
      const testResponse = await fetch(window.location.origin, { method: "HEAD" });
      const cfRay = testResponse.headers.get("cf-ray");
      if (import.meta.env.DEV && cfRay) console.log("   CF-RAY:", cfRay);
    } catch (error) {
    }
    // Geolocation now handled by server via headers
    if (import.meta.env.DEV) {
      console.log("🎯 If CF-RAY exists but no country: Enable 'Add visitor location headers'");
    }
  };

  // Safe PostHog debug helpers
  (window as any).posthogDebug = () => {
    try {
      const posthogKey = import.meta.env.VITE_POSTHOG_KEY || "";
      const posthogHost =
        import.meta.env.VITE_POSTHOG_HOST ||
        (import.meta.env.DEV ? "/ingest" : "https://us.i.posthog.com");
      if (import.meta.env.DEV) {
        console.log("PostHog key:", posthogKey ? "VITE_POSTHOG_KEY" : "Not found");
        console.log("PostHog host:", import.meta.env.VITE_POSTHOG_HOST ? "VITE_POSTHOG_HOST" : "Default");
      }
      const posthog = (window as any).posthog;
      if (posthog && import.meta.env.DEV) {
        try {
          console.log("PostHog distinctId:", posthog.get_distinct_id?.());
        } catch (error) {
        }
      }
    } catch (error) {
    }
  };

  (window as any).posthogStartRecording = () => {
    try {
      const posthog = (window as any).posthog;
      if (posthog?.startSessionRecording) {
        posthog.startSessionRecording();
      } else {
      }
    } catch (error) {
    }
  };

  (window as any).posthogStopRecording = () => {
    try {
      const posthog = (window as any).posthog;
      if (posthog?.stopSessionRecording) {
        posthog.stopSessionRecording();
      } else {
      }
    } catch (error) {
    }
  };

  (window as any).posthogTestEvent = () => {
    try {
      if ((window as any).posthogSafeCapture) {
        (window as any).posthogSafeCapture("test_event", {
          source: "console_command",
          timestamp: new Date().toISOString(),
          user_region: localStorage.getItem("user_region") || "LATAM",
        });
      } else {
      }
    } catch (error) {
    }
  };

  if (import.meta.env.DEV) {
    console.log("%c📍 Region & Country Helpers Available!", "color: #667eea; font-size: 14px; font-weight: bold");
    console.log("🌍 Regions: setRegion('US') | setRegion('SPAIN') | setRegion('LATAM')");
    console.log("🌎 LATAM Countries: testCountry('MX') | testCountry('CL') | testCountry('PY') | testCountry('BR')");
    console.log(`🚀 Cloudflare Enterprise: testCloudflareGeo() - PRIMARY geolocation (${BRAND_SITE_HOST})`);
    console.log("🔍 Cloudflare: checkCloudflareStatus() - Check Cloudflare Enterprise setup");
    console.log("🔄 Cloudflare: resetCloudflareStatus() - Reset Cloudflare availability");
    console.log("🔧 Cloudflare: forceCloudflareTest() - Force test (debug only)");
    console.log("🚨 External: testExternalGeo() - EMERGENCY backup (paid/limited APIs)");
    console.log("📊 Templates: Worker home = BaseTemplate (todos los países; ver worker.js COUNTRY_TEMPLATE_MAP)");
    console.log("🎯 PostHog: posthogDebug() | posthogStartRecording() | posthogStopRecording() | posthogTestEvent()");
    console.log("📊 PostHog Safe Capture: posthogSafeCapture('event_name', { property: 'value' }) - Safe way to capture events");
    console.log("Example: testCountry('MX') - Tests Mexico (BaseTemplate + idioma es-MX)");
    console.log("Example: posthogDebug() - Shows PostHog status and debug info");
  }
}

// Render function - ensures DOM is ready
const renderApp = () => {

  try {
    const rootElement = document.getElementById("root");
    if (!rootElement) {
      console.error("❌ Root element not found - cannot render React application");
      document.body.innerHTML =
        '<div style="padding: 20px; color: red;"><h1>Root element not found</h1></div>';
      return;
    }


    const root = createRoot(rootElement);

    root.render(<App />);
  } catch (error) {

    // Try to show a basic error message
    try {
      const rootElement = document.getElementById("root");
      if (rootElement) {
        rootElement.innerHTML = `
          <div style="padding: 20px; font-family: Arial, sans-serif; color: red; border: 2px solid red;">
            <h1>🚨 Error Loading Application</h1>
            <p><strong>Error:</strong> ${error?.message || "Unknown error"}</p>
            <p><strong>Type:</strong> ${error?.name || "Error"}</p>
            <p>Please check the browser console for more details.</p>
            <button onclick="window.location.reload()" style="padding: 10px; background: red; color: white; border: none; border-radius: 5px; cursor: pointer;">Refresh Page</button>
          </div>
        `;
      } else {
        document.body.innerHTML =
          '<div style="padding: 20px; color: red;"><h1>Critical error and root element not found</h1></div>';
      }
    } catch (fallbackError) {
    }
  }
};

// Wait for DOM to be ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", renderApp);
} else {
  // DOM is already ready, render immediately
  renderApp();
}
